/**
 * Input Validation Service
 * Validates and sanitizes user input to prevent malicious content
 */

import { appConfig } from "../config/appConfig";

interface ValidationResult {
  isValid: boolean;
  sanitizedInput: string;
  errors: string[];
  warnings: string[];
}

class InputValidatorService {
  private readonly MAX_MESSAGE_LENGTH =
    appConfig.security.inputValidation.maxMessageLength;
  private readonly MIN_MESSAGE_LENGTH =
    appConfig.security.inputValidation.minMessageLength;
  private readonly MAX_EMAIL_LENGTH =
    appConfig.security.inputValidation.maxEmailLength;

  // Patterns for detecting potentially malicious content
  private readonly SUSPICIOUS_PATTERNS = [
    /<script[^>]*>.*?<\/script>/gi, // Script tags
    /javascript:/gi, // JavaScript URLs
    /on\w+\s*=/gi, // Event handlers
    /<iframe[^>]*>.*?<\/iframe>/gi, // Iframes
    /<object[^>]*>.*?<\/object>/gi, // Objects
    /<embed[^>]*>.*?<\/embed>/gi, // Embeds
    /<link[^>]*>.*?<\/link>/gi, // Link tags
    /<meta[^>]*>.*?<\/meta>/gi, // Meta tags
    /<style[^>]*>.*?<\/style>/gi, // Style tags
  ];

  // Spam patterns
  private readonly SPAM_PATTERNS = [
    /(.)\1{10,}/g, // Repeated characters (more than 10)
    /(.)\1{4,}/g, // Repeated characters (more than 4) - warning
    /https?:\/\/[^\s]+/g, // URLs
    /www\.[^\s]+/g, // www URLs
    /[A-Z]{10,}/g, // Excessive caps
    /[!@#$%^&*()_+={}[\]|\\:";'<>?,./]{10,}/g, // Excessive special chars
  ];

  // Profanity filter (basic)
  private readonly PROFANITY_PATTERNS = [
    /\b(fuck|shit|damn|bitch|asshole|bastard)\b/gi,
    // Add more as needed
  ];

  /**
   * Validate and sanitize a chat message
   */
  validateMessage(input: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    let sanitizedInput = input.trim();

    // Length validation
    if (sanitizedInput.length < this.MIN_MESSAGE_LENGTH) {
      errors.push("Message cannot be empty");
    }

    if (sanitizedInput.length > this.MAX_MESSAGE_LENGTH) {
      errors.push(
        `Message too long. Maximum ${this.MAX_MESSAGE_LENGTH} characters allowed`
      );
      sanitizedInput = sanitizedInput.substring(0, this.MAX_MESSAGE_LENGTH);
    }

    // Check for suspicious patterns
    for (const pattern of this.SUSPICIOUS_PATTERNS) {
      if (pattern.test(sanitizedInput)) {
        errors.push("Message contains potentially malicious content");
        sanitizedInput = sanitizedInput.replace(pattern, "");
      }
    }

    // Check for spam patterns
    for (const pattern of this.SPAM_PATTERNS) {
      if (pattern.test(sanitizedInput)) {
        warnings.push("Message contains patterns that may be spam");
      }
    }

    // Check for profanity
    for (const pattern of this.PROFANITY_PATTERNS) {
      if (pattern.test(sanitizedInput)) {
        warnings.push("Message contains inappropriate language");
        sanitizedInput = sanitizedInput.replace(pattern, "***");
      }
    }

    // Basic HTML encoding for safety (but preserve apostrophes for chat)
    sanitizedInput = this.encodeHtmlForChat(sanitizedInput);

    return {
      isValid: errors.length === 0,
      sanitizedInput,
      errors,
      warnings,
    };
  }

  /**
   * Validate email address
   */
  validateEmail(email: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    let sanitizedInput = email.trim().toLowerCase();

    // Length validation
    if (sanitizedInput.length > this.MAX_EMAIL_LENGTH) {
      errors.push(
        `Email too long. Maximum ${this.MAX_EMAIL_LENGTH} characters allowed`
      );
      sanitizedInput = sanitizedInput.substring(0, this.MAX_EMAIL_LENGTH);
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedInput)) {
      errors.push("Invalid email format");
    }

    // Check for suspicious patterns in email
    if (sanitizedInput.includes("+") && sanitizedInput.split("+").length > 2) {
      warnings.push("Email contains multiple plus signs");
    }

    return {
      isValid: errors.length === 0,
      sanitizedInput,
      errors,
      warnings,
    };
  }

  /**
   * Check if input looks like spam
   */
  isSpam(input: string): {
    isSpam: boolean;
    confidence: number;
    reasons: string[];
  } {
    const reasons: string[] = [];
    let confidence = 0;

    // Check for repeated characters
    const repeatedChars = input.match(/(.)\1{4,}/g);
    if (repeatedChars) {
      reasons.push("Contains repeated characters");
      confidence += 0.3;
    }

    // Check for excessive caps
    const capsRatio = (input.match(/[A-Z]/g) || []).length / input.length;
    if (capsRatio > 0.7) {
      reasons.push("Excessive use of capital letters");
      confidence += 0.2;
    }

    // Check for URLs
    const urlCount = (input.match(/https?:\/\/[^\s]+/g) || []).length;
    if (urlCount > 2) {
      reasons.push("Contains multiple URLs");
      confidence += 0.4;
    }

    // Check for special characters
    const specialCharRatio =
      (input.match(/[!@#$%^&*()_+={}[\]|\\:";'<>?,./]/g) || []).length /
      input.length;
    if (specialCharRatio > 0.3) {
      reasons.push("Excessive special characters");
      confidence += 0.2;
    }

    // Check for very short messages with URLs
    if (input.length < 50 && urlCount > 0) {
      reasons.push("Short message with URL");
      confidence += 0.3;
    }

    return {
      isSpam: confidence > 0.5,
      confidence,
      reasons,
    };
  }

  /**
   * Encode HTML entities
   */
  private encodeHtml(text: string): string {
    const map: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };

    return text.replace(/[&<>"']/g, (match) => map[match]);
  }

  /**
   * Encode HTML entities for chat messages (preserves apostrophes)
   */
  private encodeHtmlForChat(text: string): string {
    const map: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      // Note: apostrophes are preserved for chat messages
    };

    return text.replace(/[&<>"]/g, (match) => map[match]);
  }

  /**
   * Generate a user-friendly error message
   */
  getErrorMessage(result: ValidationResult): string {
    if (result.errors.length === 0) return "";

    if (
      result.errors.includes("Message contains potentially malicious content")
    ) {
      return "Your message contains content that cannot be processed. Please remove any HTML, scripts, or special formatting.";
    }

    if (result.errors.includes("Message too long")) {
      return `Your message is too long. Please keep it under ${this.MAX_MESSAGE_LENGTH} characters.`;
    }

    if (result.errors.includes("Message cannot be empty")) {
      return "Please enter a message.";
    }

    return result.errors[0];
  }
}

export const inputValidator = new InputValidatorService();
export default InputValidatorService;
